import { useState } from "react";
import { useTranslation } from "react-i18next";
import type { KnowledgeSource } from "./types";
import { RightRailActivity } from "./RightRailActivity";
import { RightRailArtifacts } from "./RightRailArtifacts";
import { RightRailSources } from "./RightRailSources";

export type RightRailTab = "activity" | "sources" | "artifacts";

export interface TabAvailability {
  activity: boolean;
  sources: boolean;
  artifacts: boolean;
}

/** Tab-selection policy. Activity is the default on first reveal and
 *  the fallback when the user's previously-selected tab becomes empty.
 *  Sources is gated on whether the agent attached any. Artifacts is
 *  always enabled — the panel self-fetches /documents/artifacts/ and
 *  renders its own empty state when there's nothing there yet. */
export function resolveActiveTab(
  selection: RightRailTab | null,
  availability: TabAvailability,
): RightRailTab {
  if (selection === null) return "activity";
  if (selection === "activity") return "activity";
  if (selection === "sources" && availability.sources) return "sources";
  if (selection === "artifacts" && availability.artifacts) return "artifacts";
  return "activity";
}

interface RightRailProps {
  sources: readonly KnowledgeSource[];
  /** 收起右栏。父组件同时要把栅格切成两列,否则收起了也不还空间
      —— 列宽是 .teacher-grid 定的,不是这个组件能决定的。 */
  onCollapse?: () => void;
}

export function RightRail({ sources, onCollapse }: RightRailProps): JSX.Element {
  const { t } = useTranslation();
  const [selection, setSelection] = useState<RightRailTab | null>(null);

  const availability: TabAvailability = {
    activity: true,
    sources: sources.length > 0,
    // Artifacts is always enabled now that the panel owns its own
    // data fetch. The tab label drops the count badge (a periodic
    // refetch makes a numeric count noisy without much value).
    artifacts: true,
  };
  const active = resolveActiveTab(selection, availability);

  return (
    <aside
      className="teacher-rail teacher-rail--right"
      aria-label={String(t("workbench.tabs.railLabel"))}
    >
      <div className="teacher-rail-tabs" role="tablist">
        {onCollapse && (
          <button
            type="button"
            onClick={onCollapse}
            title={String(t("workbench.tabs.collapseRail"))}
            aria-label={String(t("workbench.tabs.collapseRail"))}
            className="ml-auto shrink-0 px-1.5 text-slate-400 hover:text-slate-600"
          >
            ›
          </button>
        )}
        <TabButton
          tab="activity"
          active={active === "activity"}
          enabled
          count={null}
          label={String(t("workbench.tabs.activity"))}
          onClick={() => setSelection("activity")}
        />
        {/* Sources tab is hidden entirely until the agent actually
            attaches sources — a permanently-disabled "来源 —" tab was just
            noise (it's never populated on the school deployment). */}
        {availability.sources && (
          <TabButton
            tab="sources"
            active={active === "sources"}
            enabled={availability.sources}
            count={sources.length}
            label={String(t("workbench.tabs.sources"))}
            onClick={() => setSelection("sources")}
          />
        )}
        <TabButton
          tab="artifacts"
          active={active === "artifacts"}
          enabled={availability.artifacts}
          count={null}
          label={String(t("workbench.tabs.artifacts"))}
          onClick={() => setSelection("artifacts")}
        />
      </div>

      <div className="teacher-rail-body" role="tabpanel" aria-live="polite">
        {active === "activity" && <RightRailActivity />}
        {active === "sources" && <RightRailSources sources={sources} />}
        {active === "artifacts" && <RightRailArtifacts />}
      </div>
    </aside>
  );
}

function TabButton({
  tab,
  active,
  enabled,
  count,
  label,
  onClick,
}: {
  tab: RightRailTab;
  active: boolean;
  enabled: boolean;
  count: number | null;
  label: string;
  onClick: () => void;
}): JSX.Element {
  return (
    <button
      type="button"
      role="tab"
      className={`teacher-rail-tab ${active ? "is-active" : ""}`}
      aria-selected={active}
      aria-disabled={!enabled}
      disabled={!enabled}
      data-tab={tab}
      onClick={onClick}
    >
      <span className="teacher-rail-tab__label">{label}</span>
      <span className="teacher-rail-tab__count">
        {count === null ? "" : enabled ? count : "—"}
      </span>
    </button>
  );
}
